/*
GPS DataViz

Copyright © 2021 GPSDataViz

Permission is hereby granted, free of charge, to any person obtaining a copy of this software and associated documentation files (the "Software"), to deal in the Software without restriction, including without limitation the rights to use, copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the Software, and to permit persons to whom the Software is furnished to do so, subject to the following conditions:

The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
*/
import React, { useEffect, useState } from "react";
import {
	DatePicker,
  Button
} from "antd";
import { audioMessage as message } from '../../Components/Common/Notifications/audioMessage';
import { useSelector, useDispatch } from "react-redux";
import RadarChart from '../../Components/Charts/RadarChart';
import { mapRadarData } from '../../mappers/model/RadarChart';
import BarChart from '../../Components/Charts/BarChart';
import { mapBarChartData } from '../../mappers/model/BarChartMapper';
import {
  get1v1ComparisonData,
  set1v1CompareFilterPlayerSelection,
  set1v1CompareFilterDateRange,
  set1v1CompareFilterMetrics,
  set1v1CompareData,
  set1v1CompareDataError
} from "../../store/actions/compare.actions";
import moment from "moment";
import { OptionGroupSelect } from './Components_1v1/OptionGroupSelect'
import { PlayerInfoCard } from './Components_1v1/PlayerInfoCard'
import { AggregateRow } from './Components_1v1/AggregateRow'

const { RangePicker } = DatePicker;

export const SIDE_ID = {
  LEFT: 'left',
  RIGHT: 'right'
}

export const AGGREGATE_OPTIONS = {
  AVG: 'avg',
  SUM: 'sum',
  BEST: 'best'
}

export const Comparison1V1 = ({ activeTab })  => {
  const dispatch = useDispatch();

  const {
    positions,
    playerGroups: groups,
    allPlayers,
		all_metric_types,
    info: { team_colors: teamColors },
	} = useSelector(({ team }) => team);

  const {
    filter_1v1: {
      startDate,
      endDate,
      selectionLeft,
      selectionRight,
      selectedMetrics,
    },
    data_1v1,
    error
  } = useSelector(({ compare }) => compare);

  const MAX_SELECTION_THRESHOLD = 10;
  const [loading, setLoading] = useState(false);
  const [radarChartData, setRadarChartData] = useState(null);
  const [playerPosGroupOptions, setPlayerPosGroupOptions] = useState([]);
  const [metricOptions, setMetricOptions] = useState([]);
  const [playerInfoFlexStyle, setPlayerInfoFlexStyle] = useState();
  const [aggregateDataByMetric, setAggregateDataByMetric] = useState(null);
  const [barChartDataByMetric, setBarChartDataByMetric] = useState(null);
  const [metricAnnotationMap, setMetricAnnotationMap] = useState({});

  const resetAllValues = () => {
    dispatch(set1v1CompareFilterDateRange(null));
    dispatch(set1v1CompareFilterMetrics([]));
    dispatch(set1v1CompareFilterPlayerSelection([], SIDE_ID.LEFT));
    dispatch(set1v1CompareFilterPlayerSelection([], SIDE_ID.RIGHT));
    dispatch(set1v1CompareDataError(null));
    dispatch(set1v1CompareData({}));
    setLoading(false);
    setRadarChartData(null);
    setPlayerInfoFlexStyle();
    setAggregateDataByMetric(null);
    setBarChartDataByMetric(null);
  }

  useEffect(() => {
    resetAllValues();
  }, [])

  useEffect(() => {
    resetAllValues();
  }, [activeTab])

  const getOptionGroup = (optionGroupName, JSONOptions, metric_type) => {
    let metricObj = {}, metricOps = [];
    metricObj["label"] = <span>{optionGroupName}</span>;
    metricObj["title"] = optionGroupName;
    
    for (let i = 0; i < JSONOptions.length; i++) {
      const metric = JSONOptions[i];
      if (["gps", "forceplate", "testing", "wellness", "nutrition"].includes(metric_type)) {
        metricOps.push({
          label: <span>{metric["name"]}</span>,
          value: `${metric_type}-${metric["metric_id"]}`,
          key: metric["metric_id"]
        });
      }
      else if (metric_type === "player") {
        metricOps.push({
          label: <span>{`${metric["first_name"]} ${metric["last_name"]}`}</span>,
          value: `${metric_type}-${metric["player_id"]}`,
          key: metric["player_id"]
        });
      }
      else if (metric_type === "pos") {
        metricOps.push({
          label: <span>{metric}</span>,
          value: `${metric_type}-${metric}`,
          key: metric
        });
      }
      else if (metric_type === "group") {
        metricOps.push({
          label: <span>{metric["name"]}</span>,
          value: `${metric_type}-${metric["player_group_id"]}`,
          key: metric["player_group_id"]
       });
      }
    }

    if (metric_type === "gps") {
      metricObj["options"] = metricOps;
    } else {
      metricObj["options"] = metricOps.sort((a, b) => {
        const valueA = a?.label?.props?.children || "";
        const valueB = b?.label?.props?.children || "";
        return valueA.localeCompare(valueB);
      });
    }
    return metricObj;
  }

  useEffect(() => {
    let groupOptions = [];
    for (const [metric_type, metrics] of Object.entries(all_metric_types)) {
      const metricsFiltered = metrics?.filter((e) => e.visible === true || e.visible === undefined)
      if (metricsFiltered.length > 0) {
        let groupLabel;
        if (metric_type === "gps") groupLabel = "GPS";
        else groupLabel = metric_type.charAt(0).toUpperCase() + metric_type.slice(1).toLowerCase(); //Capitalize
        groupOptions.push(getOptionGroup(groupLabel, metricsFiltered, metric_type));
      }
    }

    setMetricOptions(groupOptions)
  }, [all_metric_types])

  useEffect(() => {
    let groupOptions = [], metricObj = {};
    metricObj["label"] = <span>Team</span>;
    metricObj["title"] = "Team";
    metricObj["options"] = [{
      label: <span>Full Team</span>,
      value: "full-team",
      key: "full-team"
    }];
    
    groupOptions.push(metricObj);

    if (allPlayers.length > 0) {
      groupOptions.push(getOptionGroup("Players", allPlayers, "player"));
    }

    if (positions.length > 0) {
      groupOptions.push(getOptionGroup("Positions", positions, "pos"));
    }

    if (groups.length > 0) {
      groupOptions.push(getOptionGroup("Groups", groups, "group"));
    }

    setPlayerPosGroupOptions(groupOptions);
    dispatch(set1v1CompareFilterPlayerSelection(['full-team'], SIDE_ID.LEFT));
    dispatch(set1v1CompareFilterPlayerSelection(['full-team'], SIDE_ID.RIGHT));
  }, [positions, groups, allPlayers])

  const handleDateRangeChange = (datesMoments, dateStrings) => {
    dispatch(set1v1CompareFilterDateRange(datesMoments[0], datesMoments[1]));
  }

  const handleOptionGroupLeftChange = (value) => {
    if (value.length > MAX_SELECTION_THRESHOLD) {
      message.warning(`You can't select more than ${MAX_SELECTION_THRESHOLD} players, positions, groups.`)
    }
    else {
      dispatch(set1v1CompareFilterPlayerSelection(value, SIDE_ID.LEFT));
    }
  }

  const handleOptionGroupRightChange = (value) => {
    if (value.length > MAX_SELECTION_THRESHOLD) {
      message.warning(`You can't select more than ${MAX_SELECTION_THRESHOLD} players, positions, groups.`)
    }
    else {
      dispatch(set1v1CompareFilterPlayerSelection(value, SIDE_ID.RIGHT));
    }
  }

  const handleMetricOptionGroupChange = (value) => {
    if (value.length > MAX_SELECTION_THRESHOLD) {
      message.warning(`You can't select more than ${MAX_SELECTION_THRESHOLD} metrics.`)
    }
    else {
      dispatch(set1v1CompareFilterMetrics(value));
    }
  }

  useEffect(() => {
    if ((selectionLeft.length > 0 && !(selectionLeft.length === 1 && selectionLeft[0] === 'full-team')) && 
        (selectionRight.length > 0 && !(selectionRight.length === 1 && selectionRight[0] === 'full-team'))) {
      setPlayerInfoFlexStyle({
        justifyContent: 'space-between'
      })
    }
    else if (selectionLeft.length > 0 && !(selectionLeft.length === 1 && selectionLeft[0] === 'full-team')) {
      setPlayerInfoFlexStyle({
        justifyContent: 'flex-start'
      })
    }
    else if (selectionRight.length > 0 && !(selectionRight.length === 1 && selectionRight[0] === 'full-team')) {
      setPlayerInfoFlexStyle({
        justifyContent: 'flex-end'
      })
    }
  }, [selectionLeft, selectionRight]);

  useEffect(() => {
    if (data_1v1 && Object.keys(data_1v1).length > 0) {
      setLoading(false);
      message.success("Data retrieved successfully!");
      const metricNamesAndIds = data_1v1?.radar_data?.x_axis || []
      extractRadarChartData(data_1v1?.radar_data);
      extractBarChartData(data_1v1?.bar_data, metricNamesAndIds);
      extractAggregateData(data_1v1?.aggregates, metricNamesAndIds);
    }
  }, [data_1v1])


  useEffect(() => {
    if (data_1v1 && Object.keys(data_1v1).length > 0 && metricAnnotationMap) {
      const metricNamesAndIds = data_1v1?.radar_data?.x_axis || []
      extractRadarChartData(data_1v1?.radar_data);
      extractBarChartData(data_1v1?.bar_data, metricNamesAndIds);
    }
  }, [metricAnnotationMap])


  useEffect(() => {
    if (error) {
      setLoading(false);
      message.error(error);
      dispatch(set1v1CompareDataError(null));
    }
  }, [error])

  const extractAggregateData = (data, metricNamesAndIds) => {
    if (!data) return;
    const dataForSelectedAggregateOption = metricNamesAndIds.map( (metric) => {
      const leftMetric = data?.left?.find((metricData) => metricData.key === metric.identifier)
      const rightMetric = data?.right?.find((metricData) => metricData.key === metric.identifier)

      const aggregateObject = {
        ...metric,
        leftMetric,
        rightMetric,
        colorLeft: teamColors.all[0],
        colorRight: teamColors.all[1]
      }

      return aggregateObject;
    })

    setAggregateDataByMetric(dataForSelectedAggregateOption);
  }

  const extractRadarChartData = (data) => {
    if (!data) return;

    const maxValuesPerCategory = data?.team?.best;
    let leftSideData = [], rightSideData = [], teamSideData = [];
    if ((metricAnnotationMap !== undefined || metricAnnotationMap !== null) && data) {
      const x_axis = data.x_axis;
      
      if (x_axis) {
        for (let i = 0; i < x_axis.length; i++) {
          let annotationToUse = "avg";
          const metric_identifier = x_axis[i].identifier;
          const metricAnnot = metricAnnotationMap[metric_identifier];
          if (metricAnnot !== undefined) {
            annotationToUse = metricAnnot;
          }
          
          if (data.left) {
            leftSideData.push(data.left[annotationToUse][i])
          }

          if (data.right) {
            rightSideData.push(data.right[annotationToUse][i])
          }

          if (data.team) {
            teamSideData.push(data.team[annotationToUse][i])
          }
        }
      }
    }

    const leftSide = { data: addCustomInformationToRadarSeries(leftSideData, maxValuesPerCategory), name: `Series 1`, pointPlacement: 'on', selected: true}
    const rightSide = { data: addCustomInformationToRadarSeries(rightSideData, maxValuesPerCategory), name: `Series 2`, pointPlacement: 'on', selected: true}
    const teamSide = { data: addCustomInformationToRadarSeries(teamSideData, maxValuesPerCategory), name: 'Team', pointPlacement: 'on', selected: false, visible: false}
    const xAxis = data.x_axis?.map( (item) => item.name) || []
    const tooltip = {
      shared: true,
      formatter: function () {
            const metricLine = `Metric: ${this.x} <br>`
            const lines = this.points.map( point => {
              let annotationTooltip = "avg";
              if (data && data.x_axis) {
                const metric_key_id_obj =  data.x_axis.filter(axis_data => axis_data.name === point.key); 
                if (metric_key_id_obj) {
                  const metric_identifier = metric_key_id_obj[0].identifier;
                  const metricAnnot = metricAnnotationMap[metric_identifier];
                  if (metricAnnot !== undefined) {
                    annotationTooltip = metricAnnot;
                  }
                }
              }
              
              return `${point?.series?.name || ''} ${annotationTooltip}: <strong>${point?.point?.rawValue || 0}</strong> (${point?.point?.y || 0}% of team best)`
            })
            return metricLine + lines.join('<br>');
      }
    }
    const radarData = mapRadarData([leftSide, rightSide, teamSide], xAxis, teamColors.all, tooltip)
    setRadarChartData(radarData);
  }

  const addCustomInformationToRadarSeries = (series = [], maxValuesPerCategory = []) => {
    const customizedSeries = series?.map( (rawValue, index) => {
      const maxValue = maxValuesPerCategory[index];
      if(!maxValue) {
        return {
          y: 0,
          rawValue
        }
      }

      const scaledPoint = Number(((rawValue / maxValue) * 100).toFixed(2));
      const customDataPoint = {
        y: scaledPoint,
        rawValue
      }

      return customDataPoint
    })
    return customizedSeries
  }

  const extractBarChartData = (bar_data, metricNamesAndIds) => {
    if (!bar_data) return;
    const barDataBySeries = {};
    const categories = bar_data.map( (data) => data.disp_date)
    metricNamesAndIds.forEach( (metric) => {
      const metric_identifier = metric.identifier;

      let annotationToUse = "avg";
      if (metricAnnotationMap !== undefined || metricAnnotationMap !== null) {
        const metricAnnot = metricAnnotationMap[metric_identifier];
        if (metricAnnot !== undefined) {
          annotationToUse = metricAnnot;
        }
      }

      barDataBySeries[metric_identifier] = {
        [SIDE_ID.LEFT]: {
          name: `Series 1`,
          data: bar_data.map( (data_point_by_date) => {
            return { y: data_point_by_date[`${metric_identifier}-${SIDE_ID.LEFT}-${annotationToUse}`] }
          })
        },
        [SIDE_ID.RIGHT]: {
          name: `Series 2`,
          data: bar_data.map( (data_point_by_date) => {
            return { y: data_point_by_date[`${metric_identifier}-${SIDE_ID.RIGHT}-${annotationToUse}`] }
          })
        },
        "perc_diff": {
          name: '% Diff',
          type: 'scatter',
          yAxis: 0,
          data: bar_data.map( (data_point_by_date) => {
            let left_val = data_point_by_date[`${metric_identifier}-${SIDE_ID.LEFT}-${annotationToUse}`];
            let right_val = data_point_by_date[`${metric_identifier}-${SIDE_ID.RIGHT}-${annotationToUse}`];
            const difference = left_val - right_val;  // Absolute difference between the numbers
            let percentageDiff = 0
            const maxVal = Math.max(left_val, right_val);
            if (maxVal > 0) {
              percentageDiff = 100 * (difference / maxVal);  // Calculate percentage difference
              percentageDiff = Math.round(percentageDiff * 10) / 10;  // round
            }
            return { 
              y: maxVal,
              marker: {
                enabled: false
            },
            dataLabels: {
              enabled: true,
              style: {
                textOutline: 'none',
                fontWeight: 'normal',
                fontSize: '11px',
              },
              backgroundColor: 'white',
              borderRadius: 12,
              borderWidth: 1,
              borderColor: 'black',
              padding: 5,
              shape: 'circle',
              verticalAlign: 'middle',
              align: 'center',
              y: -20,
              x: 0,
              useHTML: true,
              formatter: function() {
                return '<div style="width:34px;height:34px;line-height:34px;text-align:center;z-index:0;">' + `${percentageDiff > 0 ? '+' : ''}${percentageDiff}%` + '</div>';
              }
            }
          }}),
        },
        categories,
        title: metric.name
      }
    })
    
    setBarChartDataByMetric({...barDataBySeries});
  }

  const convertStringToMoment = (dateString) => {
    if (!dateString) {
      return null;
    }
    return moment(dateString);
  }

  const handleSubmitClick = () => {
    if (!startDate && !startDate) {
      message.error("Please select a valid date range");
      return
    }

    if (!(selectedMetrics && selectedMetrics.length > 0)) {
      message.error("Please select valid metrics values");
      return
    }

    if (!(selectionLeft && selectionLeft.length > 0)) {
      message.error("Please select valid values from left drop-down menu for team/player/position/group");
      return
    }
      
    if (!(selectionRight && selectionRight.length > 0)) {
      message.error("Please select valid values from right drop-down menu for team/player/position/group");
      return
    }
     
    setLoading(true);
    dispatch(get1v1ComparisonData());
  }

  const handleMetricAnnotationChange = (metric, annotation) => {
    setMetricAnnotationMap({ ...metricAnnotationMap, [metric]: annotation });
  }

  return (
    <>
      <div className="compare-vs-container">
        <div className="data-filter-container">
          {/* 
            date selection left and right
          */}
          <RangePicker
            onChange={handleDateRangeChange}
            value={(!startDate && !endDate) ? null : [convertStringToMoment(startDate), convertStringToMoment(endDate)]}
            style={{ width: 250 }}
          />
          {/* 
            metric selection left and right
          */}
          <div className="metric-multi-select-container"> 
            <OptionGroupSelect 
              selectedOptionVal={selectedMetrics}
              options={metricOptions}
              placeholder="Choose Metric"
              width={756}
              handleOptionGroupChange={handleMetricOptionGroupChange}
            />
          </div>
          {/* 
            player selection left and right
          */}
          <div className="player-group-pos-container">
            {/* left */}
            <OptionGroupSelect 
              className="player-group-pos-left" 
              selectedOptionVal={selectionLeft}
              options={playerPosGroupOptions}
              placeholder="Full Team"
              width={500}
              sideId={SIDE_ID.LEFT}
              handleOptionGroupChange={handleOptionGroupLeftChange}
            />
            {/* right */}
            <OptionGroupSelect 
              className="player-group-pos-right"
              selectedOptionVal={selectionRight}
              options={playerPosGroupOptions}
              placeholder="Full Team"
              width={500}
              sideId={SIDE_ID.RIGHT}
              handleOptionGroupChange={handleOptionGroupRightChange}
            />
          </div>
          {/* 
            player info card left and right
          */}
          <div className="player-info-card-container" style={playerInfoFlexStyle}>
              {/* left */}
              <PlayerInfoCard 
                selectionList={selectionLeft}
                sideId={SIDE_ID.LEFT}
              />

              {/* right */}
              <PlayerInfoCard 
                selectionList={selectionRight}
                sideId={SIDE_ID.RIGHT}
              />
          </div>
          {/* 
            submit button
          */}
          <div className="submit-button-container">
            <Button
              className="compare-1v1-submit-button"
              type="primary"
              onClick={handleSubmitClick}
              loading={loading}
            >
              Submit
            </Button>
          </div>

          {/* 
            show metrics in progress bar left and right.
            use segmented component in middle to select from avg/best/sum
          */}
          {aggregateDataByMetric ? (
            <div className="aggregate-container">
              {aggregateDataByMetric.map((aggregateObject, index) => {
                return (
                  <div key={index} className="aggregate-row">
                    <AggregateRow 
                      data={aggregateObject} 
                      handleMetricAnnotationChange={handleMetricAnnotationChange}
                    />
                  </div>
                );
              })}
            </div>
          ) : <></>}

          {/* 
            radar/spiderweb chart
          */}
          {radarChartData ? (
            <div className="radar-chart-container">
              <RadarChart options={radarChartData}/>
            </div>
          ) : <></>}

          {/* 
            bar charts
            two charts per row
          */}
          {barChartDataByMetric ? (
            <div className="bar-chart-container">
              {Object.keys(barChartDataByMetric).map((metricIdentifier, index) => {
                const chartData = barChartDataByMetric[metricIdentifier]
                const seriesList = [chartData[SIDE_ID.LEFT], chartData[SIDE_ID.RIGHT], chartData["perc_diff"]]
                const options = mapBarChartData({
                  seriesList, categories: chartData.categories, title: chartData.title, colors: teamColors.all
                })
                return (
                  <div key={index} className="bar-chart-item">
                    <BarChart 
                      options={options}
                    />
                  </div>
                );
              })}
            </div>
          ) : <></>}
        </div>
      </div>
    </>
  );
}
